import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, Plus, Trash2, Send, LogOut, Mail, Users, 
  Calendar, TrendingUp, Eye, X, Save, Edit, Copy, CheckCircle, Image,
  Pause, Play, RefreshCw, Clock, AlertCircle, AlertTriangle, XCircle,
  ChevronDown, ChevronRight, FileText, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [viewingCampaign, setViewingCampaign] = useState(null); // Pour voir le contenu d'une campagne
  const [user, setUser] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null); // Pour le mode édition
  
  // Suivi des campagnes en cours d'envoi
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingCampaignId, setTrackingCampaignId] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  
  // Journal des échecs
  const [showFailuresModal, setShowFailuresModal] = useState(false);
  const [failuresData, setFailuresData] = useState(null);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [selectedFailureCampaign, setSelectedFailureCampaign] = useState(null);
  const [expandedFailures, setExpandedFailures] = useState({});

  // Formulaire campagne
  const [formData, setFormData] = useState({
    name: '',
    type: 'newsletter',
    subject: '',
    body_html: '',
    body_text: '',
    recipient_filter: {}
  });

  // Filtres destinataires
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterTags, setFilterTags] = useState('');
  const [filterInterets, setFilterInterets] = useState([]);
  const [filterDegreInteret, setFilterDegreInteret] = useState('');
  const [filterExcludeCampaign, setFilterExcludeCampaign] = useState(''); // Exclure contacts ayant reçu cette campagne
  const [recipientCount, setRecipientCount] = useState(0);
  const [sentCampaigns, setSentCampaigns] = useState([]); // Liste des campagnes envoyées pour le filtre

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const pageSize = 15;
  const filteredCampaigns = filterType ? campaigns.filter(c => c.type === filterType) : campaigns;

  // Paramètres app
  const [appSettings, setAppSettings] = useState({ 
    contact_statuts: [],
    contact_sources: [],
    contact_interets: [], 
    contact_degres_interet: [],
    campaign_types: []
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    verifyAuth();
  }, []);

  // Charger les paramètres après authentification
  useEffect(() => {
    if (user) {
      fetchAppSettings();
      fetchCampaigns();
      fetchStats();
    }
  }, [user]);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/app`, getAuthHeaders());
      console.log('Paramètres app chargés:', response.data);
      setAppSettings(response.data);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Erreur chargement paramètres app:', error);
    }
  };

  const fetchSentCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns?status=sent&limit=100`, getAuthHeaders());
      setSentCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Erreur chargement campagnes envoyées:', error);
    }
  };

  // Fonctions pour le journal des échecs
  const fetchAllFailures = async () => {
    setFailuresLoading(true);
    try {
      const response = await axios.get(`${API}/email-failures?limit=100`, getAuthHeaders());
      setFailuresData(response.data);
      setSelectedFailureCampaign(null);
    } catch (error) {
      console.error('Erreur chargement échecs:', error);
      toast.error('Erreur lors du chargement des échecs');
    } finally {
      setFailuresLoading(false);
    }
  };

  const fetchCampaignFailures = async (campaignId) => {
    setFailuresLoading(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/failures`, getAuthHeaders());
      setFailuresData(response.data);
      setSelectedFailureCampaign(campaignId);
    } catch (error) {
      console.error('Erreur chargement échecs campagne:', error);
      toast.error('Erreur lors du chargement des échecs');
    } finally {
      setFailuresLoading(false);
    }
  };

  const openFailuresModal = (campaignId = null) => {
    setShowFailuresModal(true);
    setExpandedFailures({});
    if (campaignId) {
      fetchCampaignFailures(campaignId);
    } else {
      fetchAllFailures();
    }
  };

  const toggleFailureExpand = (index) => {
    setExpandedFailures(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Charger les campagnes envoyées au démarrage
  useEffect(() => {
    if (user) {
      fetchSentCampaigns();
    }
  }, [user]);

  // Suivi en temps réel des campagnes en cours d'envoi
  useEffect(() => {
    let interval;
    if (showTrackingModal && trackingCampaignId) {
      // Mettre à jour immédiatement
      fetchTrackingData();
      
      // Puis toutes les 5 secondes
      interval = setInterval(fetchTrackingData, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showTrackingModal, trackingCampaignId]);

  const fetchTrackingData = async () => {
    if (!trackingCampaignId) return;
    try {
      const response = await axios.get(
        `${API}/campaigns/${trackingCampaignId}/status`,
        getAuthHeaders()
      );
      setTrackingData(response.data);
      
      // Si la campagne est terminée, arrêter le suivi automatique
      if (!['sending'].includes(response.data.status)) {
        // Actualiser la liste des campagnes
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Erreur suivi campagne:', error);
    }
  };

  useEffect(() => {
    if (showModal && user) {
      updateRecipientCount();
    }
  }, [filterStatut, filterSource, filterTags, filterInterets, filterDegreInteret, filterExcludeCampaign, showModal, user]);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns?limit=1000`, getAuthHeaders());
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Erreur chargement campagnes:', error);
      toast.error('Erreur lors du chargement des campagnes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const updateRecipientCount = async () => {
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterSource) params.source = filterSource;
      if (filterTags) params.tags = filterTags;
      if (filterInterets.length > 0) params.interets = filterInterets.join(',');
      if (filterDegreInteret) params.degre_interet = filterDegreInteret;
      if (filterExcludeCampaign) params.exclude_campaign = filterExcludeCampaign;

      const response = await axios.get(`${API}/contacts`, {
        ...getAuthHeaders(),
        params: { ...params, limit: 1 }
      });
      setRecipientCount(response.data.total);
    } catch (error) {
      console.error('Erreur comptage destinataires:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const openModal = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      type: 'newsletter',
      subject: '',
      body_html: '',
      body_text: '',
      recipient_filter: {}
    });
    setFilterStatut('');
    setFilterSource('');
    setFilterTags('');
    setFilterInterets([]);
    setFilterDegreInteret('');
    setFilterExcludeCampaign('');
    setRecipientCount(0);
    setShowModal(true);
  };

  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      type: campaign.type,
      subject: campaign.subject,
      body_html: campaign.body_html,
      body_text: campaign.body_text,
      recipient_filter: campaign.recipient_filter || {}
    });
    // Restaurer les filtres
    const rf = campaign.recipient_filter || {};
    setFilterStatut(rf.statut || '');
    setFilterSource(rf.source || '');
    setFilterTags(Array.isArray(rf.tags) ? rf.tags.join(', ') : '');
    setFilterInterets(Array.isArray(rf.interets) ? rf.interets : []);
    setFilterDegreInteret(rf.degre_interet || '');
    setFilterExcludeCampaign(rf.exclude_campaign || '');
    setRecipientCount(campaign.total_recipients || 0);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCampaign(null);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Construire recipient_filter
    const recipient_filter = {};
    if (filterStatut) recipient_filter.statut = filterStatut;
    if (filterSource) recipient_filter.source = filterSource;
    if (filterTags) recipient_filter.tags = filterTags.split(',').map(t => t.trim()).filter(t => t);
    if (filterInterets.length > 0) recipient_filter.interets = filterInterets;
    if (filterDegreInteret) recipient_filter.degre_interet = filterDegreInteret;
    if (filterExcludeCampaign) recipient_filter.exclude_campaign = filterExcludeCampaign;
    
    const campaignData = {
      ...formData,
      recipient_filter
    };
    
    try {
      if (editingCampaign) {
        // Mode modification
        await axios.patch(`${API}/campaigns/${editingCampaign.id}`, campaignData, getAuthHeaders());
        toast.success('Campagne modifiée avec succès');
      } else {
        // Mode création
        await axios.post(`${API}/campaigns`, campaignData, getAuthHeaders());
        toast.success('Campagne créée avec succès');
      }
      closeModal();
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Erreur campagne:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    }
  };

  const handleDuplicate = async (campaignId, campaignName) => {
    if (!window.confirm(`Voulez-vous dupliquer la campagne "${campaignName}" ?`)) {
      return;
    }

    try {
      await axios.post(`${API}/campaigns/${campaignId}/duplicate`, {}, getAuthHeaders());
      toast.success('Campagne dupliquée avec succès');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Erreur duplication:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const handleSend = async (campaignId, campaignName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir envoyer la campagne "${campaignName}" ?\n\nL'envoi se fera en arrière-plan et respectera la limite de votre hébergeur.`)) {
      return;
    }

    try {
      // Étape 1: Valider et corriger les images
      toast.info('Validation des images...', { duration: 2000 });
      const validationResponse = await axios.post(
        `${API}/campaigns/${campaignId}/validate-images`, 
        {}, 
        getAuthHeaders()
      );
      
      if (validationResponse.data.total_images > 0) {
        const issues = validationResponse.data.images.filter(img => img.issues.length > 0);
        if (issues.length > 0) {
          if (validationResponse.data.html_corrected) {
            toast.success('✅ Images corrigées automatiquement', { duration: 3000 });
          }
        }
      }
      
      // Étape 2: Démarrer l'envoi en arrière-plan
      const response = await axios.post(`${API}/campaigns/${campaignId}/send`, {}, getAuthHeaders());
      
      // Afficher les informations de l'envoi
      toast.success(
        <div>
          <strong>🚀 Envoi démarré !</strong>
          <p className="text-sm mt-1">{response.data.total_to_send} emails à envoyer</p>
          <p className="text-sm">Durée estimée : {response.data.estimated_duration}</p>
        </div>,
        { duration: 6000 }
      );
      
      fetchCampaigns();
      fetchStats();
      
      // Ouvrir le modal de suivi
      setTrackingCampaignId(campaignId);
      setShowTrackingModal(true);
      
    } catch (error) {
      console.error('Erreur envoi:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/pause`, {}, getAuthHeaders());
      toast.success('Campagne mise en pause');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise en pause');
    }
  };

  const handleResume = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/send`, {}, getAuthHeaders());
      toast.success(`Envoi repris - ${response.data.total_to_send} emails restants`);
      fetchCampaigns();
      setTrackingCampaignId(campaignId);
      setShowTrackingModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la reprise');
    }
  };

  const handleReset = async (campaignId, campaignName) => {
    if (!window.confirm(`Réinitialiser la campagne "${campaignName}" ?\n\nTous les statuts d'envoi seront remis à zéro.`)) {
      return;
    }
    try {
      await axios.post(`${API}/campaigns/${campaignId}/reset`, {}, getAuthHeaders());
      toast.success('Campagne réinitialisée');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const handleValidateImages = async (campaignId, campaignName) => {
    try {
      toast.info('Validation des images en cours...', { duration: 2000 });
      const response = await axios.post(
        `${API}/campaigns/${campaignId}/validate-images`, 
        {}, 
        getAuthHeaders()
      );
      
      const { total_images, images, html_corrected } = response.data;
      
      if (total_images === 0) {
        toast.info('ℹ️ Aucune image dans cette campagne', { duration: 3000 });
        return;
      }
      
      const errors = images.filter(img => img.status === 'error');
      const warnings = images.filter(img => img.status === 'warning');
      
      if (errors.length > 0) {
        // Construire un message détaillé pour les erreurs
        const errorDetails = errors.map(img => {
          const issues = img.issues.join(', ');
          return `• ${issues}`;
        }).join('\n');
        
        toast.error(
          <div>
            <strong>❌ {errors.length} image(s) inaccessible(s)</strong>
            <p className="text-sm mt-1">Vérifiez que les images sont uploadées et accessibles publiquement.</p>
          </div>,
          { duration: 6000 }
        );
      } else if (warnings.length > 0) {
        if (html_corrected) {
          // Construire un message détaillé pour les corrections
          const correctionDetails = warnings.map(img => {
            if (img.issues.includes('URL preview remplacée par URL production')) {
              return '• URL corrigée pour le domaine de production';
            }
            if (img.issues.includes('URL relative convertie en URL publique')) {
              return '• URL relative convertie en URL publique';
            }
            if (img.issues.includes('Espaces encodés dans l\'URL')) {
              return '• Espaces dans le nom de fichier corrigés';
            }
            return `• ${img.issues.join(', ')}`;
          }).join('\n');
          
          toast.success(
            <div>
              <strong>✅ {warnings.length} image(s) corrigée(s) automatiquement</strong>
              <p className="text-sm mt-1">Les URLs ont été mises à jour pour fonctionner dans les emails.</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          // Avertissements sans correction possible
          const warningDetails = warnings.map(img => img.issues.join(', ')).join('; ');
          toast.warning(
            <div>
              <strong>⚠️ {warnings.length} image(s) avec avertissements</strong>
              <p className="text-sm mt-1">{warningDetails}</p>
              <p className="text-xs mt-1">Les images peuvent ne pas s'afficher correctement dans tous les clients email.</p>
            </div>,
            { duration: 6000 }
          );
        }
      } else {
        toast.success(
          <div>
            <strong>✅ {total_images} image(s) validée(s)</strong>
            <p className="text-sm mt-1">Toutes les images sont accessibles. Prêt pour l'envoi !</p>
          </div>,
          { duration: 4000 }
        );
      }
      
      // Rafraîchir les campagnes si corrections appliquées
      if (html_corrected) {
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Erreur validation:', error);
      toast.error('Erreur lors de la validation des images');
    }
  };

  const handleDelete = async (campaignId, campaignName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la campagne "${campaignName}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, getAuthHeaders());
      toast.success('Campagne supprimée avec succès');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // URL du logo Refacing VR
  const LOGO_URL = 'https://customer-assets.emergentagent.com/job_default-contact-data/artifacts/z94gkda7_Screenshot%202025-11-21%20at%2005.46.32.png';

  // Templates d'email disponibles
  const emailTemplates = {
    newsletter: {
      name: 'Newsletter',
      description: 'Infolettre avec bannière et sections',
      html: `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Infolettre Refacing VR</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f5f0; font-family: Arial, Helvetica, sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header avec logo -->
          <tr>
            <td style="background-color: #ffffff; padding: 25px 30px; border-bottom: 3px solid #335C2E;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td width="200">
                    <img src="${LOGO_URL}" alt="Refacing VR" style="max-width: 180px; height: auto;" />
                  </td>
                  <td align="right" style="color: #333333; font-size: 12px; text-transform: uppercase; letter-spacing: 1px;">
                    <strong style="color: #335C2E;">INFOLETTRE</strong><br>
                    <span style="color: #C7A027;">DIVISION PAVILLONS</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Bannière image principale -->
          <tr>
            <td style="padding: 0;">
              <!-- REMPLACEZ L'URL CI-DESSOUS PAR VOTRE IMAGE DE BANNIÈRE -->
              <img src="https://via.placeholder.com/600x300/335C2E/FFFFFF?text=Votre+Image+de+Banni%C3%A8re" alt="Bannière" style="width: 100%; height: auto; display: block;" />
            </td>
          </tr>
          
          <!-- Titre de la newsletter -->
          <tr>
            <td style="padding: 30px 30px 15px 30px;">
              <h1 style="margin: 0; color: #333333; font-size: 24px; font-weight: bold; text-align: center;">
                Titre de votre infolettre
              </h1>
            </td>
          </tr>
          
          <!-- Contenu principal -->
          <tr>
            <td style="padding: 15px 30px 30px 30px; color: #333333; font-size: 15px; line-height: 1.7;">
              <p style="margin: 0 0 15px 0;">Bonjour {{prenom}},</p>
              
              <p style="margin: 0 0 15px 0;">
                Écrivez votre contenu ici. Vous pouvez ajouter des paragraphes, des liens, 
                et personnaliser ce template selon vos besoins.
              </p>
              
              <p style="margin: 0 0 15px 0;">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod 
                tempor incididunt ut labore et dolore magna aliqua.
              </p>
              
              <!-- Bouton CTA -->
              <table cellpadding="0" cellspacing="0" border="0" style="margin: 25px auto;">
                <tr>
                  <td align="center" style="background-color: #335C2E; border-radius: 5px;">
                    <a href="https://refacingvr.com" target="_blank" style="display: inline-block; padding: 14px 35px; color: #ffffff; text-decoration: none; font-weight: bold; font-size: 14px;">
                      EN SAVOIR PLUS
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Séparateur -->
          <tr>
            <td style="padding: 0 30px;">
              <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 0;" />
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 25px 30px; background-color: #f8f8f8; text-align: center;">
              <p style="margin: 0 0 10px 0; color: #333333; font-size: 14px; font-weight: bold;">
                Refacing VR - Division Pavillons
              </p>
              <p style="margin: 0 0 5px 0; color: #666666; font-size: 12px;">
                4000, montée Masson, Laval (Québec), H7B 1B7
              </p>
              <p style="margin: 0; color: #666666; font-size: 12px;">
                <a href="mailto:info@refacingvr.com" style="color: #335C2E;">info@refacingvr.com</a> | 
                <a href="tel:5142131783" style="color: #335C2E;">514 213-1783</a>
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
    },
    
    promotion: {
      name: 'Promotionnel',
      description: 'Offre spéciale avec mise en avant',
      html: `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Offre Spéciale Refacing VR</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f5f0; font-family: Arial, Helvetica, sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header avec logo -->
          <tr>
            <td style="background-color: #335C2E; padding: 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center">
                    <img src="${LOGO_URL}" alt="Refacing VR" style="max-width: 200px; height: auto; filter: brightness(0) invert(1);" />
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Bannière promo -->
          <tr>
            <td style="background: linear-gradient(135deg, #335C2E 0%, #4a7a3f 100%); padding: 40px 30px; text-align: center;">
              <p style="margin: 0 0 10px 0; color: #C7A027; font-size: 14px; text-transform: uppercase; letter-spacing: 2px;">
                OFFRE EXCLUSIVE
              </p>
              <h1 style="margin: 0 0 15px 0; color: #ffffff; font-size: 32px; font-weight: bold;">
                Votre Titre Promotionnel
              </h1>
              <p style="margin: 0; color: rgba(255,255,255,0.9); font-size: 16px;">
                Sous-titre ou description courte de l'offre
              </p>
            </td>
          </tr>
          
          <!-- Image produit/service -->
          <tr>
            <td style="padding: 0;">
              <!-- REMPLACEZ L'URL CI-DESSOUS PAR VOTRE IMAGE -->
              <img src="https://via.placeholder.com/600x350/f5f5f0/335C2E?text=Image+Produit+ou+Projet" alt="Image" style="width: 100%; height: auto; display: block;" />
            </td>
          </tr>
          
          <!-- Contenu -->
          <tr>
            <td style="padding: 30px; color: #333333; font-size: 15px; line-height: 1.7;">
              <p style="margin: 0 0 15px 0;">Bonjour {{prenom}},</p>
              
              <p style="margin: 0 0 20px 0;">
                Découvrez notre offre exceptionnelle ! Décrivez ici les avantages 
                et les détails de votre promotion.
              </p>
              
              <!-- Points forts -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 25px;">
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #e0e0e0;">
                    <span style="color: #C7A027; font-size: 18px; margin-right: 10px;">✓</span>
                    <span style="color: #333333;">Avantage numéro 1</span>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #e0e0e0;">
                    <span style="color: #C7A027; font-size: 18px; margin-right: 10px;">✓</span>
                    <span style="color: #333333;">Avantage numéro 2</span>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 10px 0;">
                    <span style="color: #C7A027; font-size: 18px; margin-right: 10px;">✓</span>
                    <span style="color: #333333;">Avantage numéro 3</span>
                  </td>
                </tr>
              </table>
              
              <!-- Bouton CTA -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center">
                    <a href="https://refacingvr.com" target="_blank" style="display: inline-block; padding: 16px 40px; background-color: #C7A027; color: #ffffff; text-decoration: none; font-weight: bold; font-size: 15px; border-radius: 5px;">
                      PROFITER DE L'OFFRE
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 25px 30px; background-color: #333333; text-align: center;">
              <p style="margin: 0 0 10px 0; color: #ffffff; font-size: 14px; font-weight: bold;">
                Refacing VR - Division Pavillons
              </p>
              <p style="margin: 0 0 5px 0; color: #aaaaaa; font-size: 12px;">
                4000, montée Masson, Laval (Québec), H7B 1B7
              </p>
              <p style="margin: 0; color: #aaaaaa; font-size: 12px;">
                <a href="mailto:info@refacingvr.com" style="color: #C7A027;">info@refacingvr.com</a> | 
                <a href="tel:5142131783" style="color: #C7A027;">514 213-1783</a>
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
    },
    
    event: {
      name: 'Événement',
      description: 'Invitation à un événement',
      html: `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Événement Refacing VR</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f5f0; font-family: Arial, Helvetica, sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header avec logo -->
          <tr>
            <td style="background-color: #ffffff; padding: 25px 30px; text-align: center; border-bottom: 3px solid #C7A027;">
              <img src="${LOGO_URL}" alt="Refacing VR" style="max-width: 180px; height: auto;" />
            </td>
          </tr>
          
          <!-- Bannière événement -->
          <tr>
            <td style="padding: 0; position: relative;">
              <!-- REMPLACEZ L'URL CI-DESSOUS PAR VOTRE IMAGE D'ÉVÉNEMENT -->
              <img src="https://via.placeholder.com/600x300/335C2E/FFFFFF?text=Image+%C3%89v%C3%A9nement" alt="Événement" style="width: 100%; height: auto; display: block;" />
            </td>
          </tr>
          
          <!-- Badge inscription -->
          <tr>
            <td align="center" style="padding: 0;">
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="background-color: #C7A027; padding: 10px 25px; border-radius: 0 0 5px 5px;">
                    <span style="color: #ffffff; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold;">
                      INSCRIVEZ-VOUS
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Titre événement -->
          <tr>
            <td style="padding: 30px 30px 15px 30px; text-align: center;">
              <h1 style="margin: 0 0 10px 0; color: #335C2E; font-size: 28px; font-weight: bold; text-transform: uppercase;">
                NOM DE L'ÉVÉNEMENT
              </h1>
              <p style="margin: 0; color: #C7A027; font-size: 16px; font-weight: bold;">
                Sous-titre ou thème de l'événement
              </p>
            </td>
          </tr>
          
          <!-- Informations clés -->
          <tr>
            <td style="padding: 20px 30px;">
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f8f8f8; border-radius: 8px;">
                <tr>
                  <td style="padding: 20px; text-align: center; border-right: 1px solid #e0e0e0;" width="50%">
                    <p style="margin: 0 0 5px 0; color: #C7A027; font-size: 24px;">📅</p>
                    <p style="margin: 0 0 5px 0; color: #333333; font-weight: bold; font-size: 14px;">DATE</p>
                    <p style="margin: 0; color: #666666; font-size: 13px;">JJ Mois AAAA</p>
                  </td>
                  <td style="padding: 20px; text-align: center;" width="50%">
                    <p style="margin: 0 0 5px 0; color: #C7A027; font-size: 24px;">📍</p>
                    <p style="margin: 0 0 5px 0; color: #333333; font-weight: bold; font-size: 14px;">LIEU</p>
                    <p style="margin: 0; color: #666666; font-size: 13px;">Adresse de l'événement</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Contenu -->
          <tr>
            <td style="padding: 15px 30px 30px 30px; color: #333333; font-size: 15px; line-height: 1.7;">
              <p style="margin: 0 0 15px 0;">Bonjour {{prenom}},</p>
              
              <p style="margin: 0 0 15px 0;">
                Nous avons le plaisir de vous inviter à notre événement. Décrivez ici 
                les détails et ce que les participants peuvent attendre.
              </p>
              
              <p style="margin: 0 0 20px 0;">
                Ne manquez pas cette occasion unique de découvrir nos nouveautés !
              </p>
              
              <!-- Bouton CTA -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td align="center">
                    <a href="https://refacingvr.com" target="_blank" style="display: inline-block; padding: 16px 40px; background-color: #335C2E; color: #ffffff; text-decoration: none; font-weight: bold; font-size: 15px; border-radius: 5px;">
                      JE M'INSCRIS
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 25px 30px; background-color: #f8f8f8; text-align: center; border-top: 3px solid #335C2E;">
              <p style="margin: 0 0 10px 0; color: #333333; font-size: 14px; font-weight: bold;">
                Refacing VR - Division Pavillons
              </p>
              <p style="margin: 0 0 5px 0; color: #666666; font-size: 12px;">
                4000, montée Masson, Laval (Québec), H7B 1B7
              </p>
              <p style="margin: 0; color: #666666; font-size: 12px;">
                <a href="mailto:info@refacingvr.com" style="color: #335C2E;">info@refacingvr.com</a> | 
                <a href="tel:5142131783" style="color: #335C2E;">514 213-1783</a>
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
    },
    
    simple: {
      name: 'Simple',
      description: 'Template minimaliste',
      html: `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Refacing VR</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f5f0; font-family: Arial, Helvetica, sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header simple -->
          <tr>
            <td style="padding: 30px; text-align: center; border-bottom: 2px solid #335C2E;">
              <img src="${LOGO_URL}" alt="Refacing VR" style="max-width: 180px; height: auto;" />
            </td>
          </tr>
          
          <!-- Contenu -->
          <tr>
            <td style="padding: 40px 30px; color: #333333; font-size: 15px; line-height: 1.8;">
              <p style="margin: 0 0 20px 0;">Bonjour {{prenom}} {{nom}},</p>
              
              <p style="margin: 0 0 20px 0;">
                Écrivez votre message ici...
              </p>
              
              <p style="margin: 0;">
                Cordialement,<br>
                <strong style="color: #335C2E;">L'équipe Refacing VR</strong>
              </p>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 20px 30px; background-color: #f8f8f8; text-align: center; border-top: 1px solid #e0e0e0;">
              <p style="margin: 0 0 5px 0; color: #666666; font-size: 12px;">
                4000, montée Masson, Laval (Québec), H7B 1B7
              </p>
              <p style="margin: 0; color: #666666; font-size: 12px;">
                <a href="mailto:info@refacingvr.com" style="color: #335C2E;">info@refacingvr.com</a> | 
                <a href="tel:5142131783" style="color: #335C2E;">514 213-1783</a>
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
    }
  };

  // État pour la sélection de template
  const [selectedTemplate, setSelectedTemplate] = useState('newsletter');

  // Générer un template HTML simple (garde la fonction pour compatibilité) - Mémorisé
  const generateDefaultHTML = useCallback(() => {
    return emailTemplates[selectedTemplate]?.html || emailTemplates.newsletter.html;
  }, [selectedTemplate]);

  const generateDefaultText = useCallback(() => {
    return `Bonjour {{prenom}} {{nom}},

Insérez votre contenu ici...

Cordialement,
L'équipe Refacing VR

--
4000, montée Masson, Laval (Québec), H7B 1B7
info@refacingvr.com | 514 213 1783`;
  }, []);

  // Stats mémorisées
  const campaignStats = useMemo(() => {
    if (!stats) return null;
    return {
      total: stats.total_campaigns || 0,
      sent: stats.sent_count || 0,
      draft: stats.draft_count || 0
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: '#335C2E' }}>
                <Mail className="inline-block mr-2" size={28} />
                Campagnes d'emailing
              </h1>
              {stats && (
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                  {stats.total_campaigns} campagnes
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
                style={{ background: '#335C2E', color: '#FFFFFF' }}
              >
                <Home size={18} />
                Retour Admin
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
                style={{ background: '#DC3545', color: '#FFFFFF' }}
              >
                <LogOut size={18} />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-lg shadow" style={{ background: '#FFFFFF' }}>
              <div className="text-sm" style={{ color: '#666666' }}>Brouillons</div>
              <div className="text-2xl font-bold mt-1" style={{ color: '#999999' }}>
                {stats.by_status.draft}
              </div>
            </div>
            <div className="p-4 rounded-lg shadow" style={{ background: '#FFFFFF' }}>
              <div className="text-sm" style={{ color: '#666666' }}>Envoyées</div>
              <div className="text-2xl font-bold mt-1" style={{ color: '#4CAF50' }}>
                {stats.by_status.sent}
              </div>
            </div>
            <div className="p-4 rounded-lg shadow" style={{ background: '#FFFFFF' }}>
              <div className="text-sm" style={{ color: '#666666' }}>Total emails envoyés</div>
              <div className="text-2xl font-bold mt-1" style={{ color: '#335C2E' }}>
                {stats.total_emails_sent}
              </div>
            </div>
            <div className="p-4 rounded-lg shadow" style={{ background: '#FFFFFF' }}>
              <div className="text-sm" style={{ color: '#666666' }}>Taux de succès</div>
              <div className="text-2xl font-bold mt-1" style={{ color: '#C7A027' }}>
                {stats.total_emails_sent > 0 
                  ? ((stats.total_emails_sent - stats.total_emails_failed) / stats.total_emails_sent * 100).toFixed(1)
                  : 0}%
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={openModal}
            className="flex items-center gap-2 px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-lg font-medium"
            style={{ background: '#335C2E', color: '#FFFFFF' }}
          >
            <Plus size={20} />
            Nouvelle campagne
          </button>
          
          <button
            onClick={() => openFailuresModal()}
            className="flex items-center gap-2 px-4 py-3 rounded-lg hover:opacity-90 transition-opacity font-medium border"
            style={{ borderColor: '#DC2626', color: '#DC2626', background: '#FEF2F2' }}
          >
            <AlertTriangle size={18} />
            Journal des échecs
          </button>
        </div>

        {/* Filtres + Liste des campagnes */}
        <div className="rounded-lg shadow overflow-hidden" style={{ background: '#FFFFFF' }}>
          {/* Barre de filtres */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ background: '#FAFAFA' }}>
            <span className="text-sm font-medium" style={{ color: '#666' }}>Filtrer :</span>
            {['', 'automatisé', 'newsletter', 'personnalisé'].map(type => (
              <button
                key={type}
                onClick={() => { setFilterType(type); setCurrentPage(1); }}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${filterType === type ? 'text-white' : 'hover:bg-gray-100'}`}
                style={filterType === type ? { backgroundColor: '#335C2E', borderColor: '#335C2E' } : {}}
                data-testid={`filter-type-${type || 'all'}`}
              >
                {type === '' ? 'Toutes' : type === 'automatisé' ? 'Automatisé' : type === 'newsletter' ? 'Newsletter' : 'Personnalisé'}
              </button>
            ))}
            <span className="ml-auto text-xs" style={{ color: '#999' }}>
              {filteredCampaigns.length} campagne{filteredCampaigns.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#F5F5F5' }}>
                <tr>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Campagne</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Type</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Destinataires</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Envoyés</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Statut</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Date</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((campaign) => (
                  <tr key={campaign.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div>
                        <div className="font-medium" style={{ color: '#333333' }}>
                          {campaign.name}
                        </div>
                        <div className="text-xs" style={{ color: '#999999' }}>
                          {campaign.subject}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>
                      {campaign.type === 'newsletter' ? 'Newsletter' : 
                       campaign.type === 'personnalisé' ? 'Personnalisé' : 'Automatisé'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users size={14} style={{ color: '#666666' }} />
                        <span style={{ color: '#666666' }}>{campaign.total_recipients}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-sm">
                        <div style={{ color: '#4CAF50' }}>{campaign.sent_count}</div>
                        {campaign.failed_count > 0 && (
                          <div style={{ color: '#DC3545', fontSize: '11px' }}>
                            {campaign.failed_count} échecs
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: campaign.status === 'sent' ? '#E8F5E9' : 
                                     campaign.status === 'sending' ? '#FFF3E0' :
                                     campaign.status === 'draft' ? '#F5F5F5' : 
                                     campaign.status === 'partial' ? '#FFF8E1' :
                                     campaign.status === 'failed' ? '#FFEBEE' : '#FFEBEE',
                          color: campaign.status === 'sent' ? '#2E7D32' : 
                                 campaign.status === 'sending' ? '#E65100' :
                                 campaign.status === 'draft' ? '#666666' : 
                                 campaign.status === 'partial' ? '#F57F17' :
                                 campaign.status === 'failed' ? '#C62828' : '#C62828'
                        }}
                      >
                        {campaign.status === 'draft' ? 'Brouillon' :
                         campaign.status === 'sending' ? 'En cours' :
                         campaign.status === 'sent' ? 'Envoyée' : 
                         campaign.status === 'partial' ? 'Partielle' :
                         campaign.status === 'failed' ? 'Échec' : 'Annulée'}
                      </span>
                    </td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(campaign.created_at).toLocaleDateString('fr-CA')}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-center flex-wrap">
                        {/* Bouton Modifier - seulement pour brouillons */}
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => openEditModal(campaign)}
                            className="p-2 rounded hover:bg-blue-100"
                            title="Modifier"
                          >
                            <Edit size={16} style={{ color: '#2196F3' }} />
                          </button>
                        )}
                        {/* Bouton Valider Images - seulement pour brouillons */}
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => handleValidateImages(campaign.id, campaign.name)}
                            className="p-2 rounded hover:bg-cyan-100"
                            title="Valider les images"
                          >
                            <Image size={16} style={{ color: '#00BCD4' }} />
                          </button>
                        )}
                        {/* Bouton Dupliquer */}
                        <button
                          onClick={() => handleDuplicate(campaign.id, campaign.name)}
                          className="p-2 rounded hover:bg-purple-100"
                          title="Dupliquer"
                        >
                          <Copy size={16} style={{ color: '#9C27B0' }} />
                        </button>
                        {/* Bouton Voir le contenu - pour toutes les campagnes */}
                        <button
                          onClick={() => setViewingCampaign(campaign)}
                          className="p-2 rounded hover:bg-indigo-100"
                          title="Voir le contenu de l'email"
                        >
                          <Eye size={16} style={{ color: '#3F51B5' }} />
                        </button>
                        {/* Bouton Envoyer - pour brouillons ou en pause */}
                        {(campaign.status === 'draft' || campaign.status === 'paused') && (
                          <button
                            onClick={() => campaign.status === 'paused' 
                              ? handleResume(campaign.id) 
                              : handleSend(campaign.id, campaign.name)
                            }
                            className="p-2 rounded hover:bg-green-100"
                            title={campaign.status === 'paused' ? 'Reprendre l\'envoi' : 'Envoyer'}
                          >
                            {campaign.status === 'paused' ? (
                              <Play size={16} style={{ color: '#4CAF50' }} />
                            ) : (
                              <Send size={16} style={{ color: '#4CAF50' }} />
                            )}
                          </button>
                        )}
                        {/* Bouton Pause - pour campagnes en cours */}
                        {campaign.status === 'sending' && (
                          <button
                            onClick={() => handlePause(campaign.id)}
                            className="p-2 rounded hover:bg-yellow-100"
                            title="Mettre en pause"
                          >
                            <Pause size={16} style={{ color: '#FFA726' }} />
                          </button>
                        )}
                        {/* Bouton Voir progression - pour campagnes en cours */}
                        {campaign.status === 'sending' && (
                          <button
                            onClick={() => {
                              setTrackingCampaignId(campaign.id);
                              setShowTrackingModal(true);
                            }}
                            className="p-2 rounded hover:bg-blue-100"
                            title="Voir la progression"
                          >
                            <Eye size={16} style={{ color: '#2196F3' }} />
                          </button>
                        )}
                        {/* Bouton Réinitialiser - pour campagnes terminées ou en pause */}
                        {['sent', 'partial', 'failed', 'paused'].includes(campaign.status) && (
                          <>
                            {/* Bouton Voir les échecs - si la campagne a des échecs */}
                            {(campaign.failed_count > 0 || campaign.status === 'failed' || campaign.status === 'partial') && (
                              <button
                                onClick={() => openFailuresModal(campaign.id)}
                                className="p-2 rounded hover:bg-red-100"
                                title="Voir les échecs d'envoi"
                              >
                                <AlertTriangle size={16} style={{ color: '#DC2626' }} />
                              </button>
                            )}
                            <button
                              onClick={() => handleReset(campaign.id, campaign.name)}
                              className="p-2 rounded hover:bg-orange-100"
                              title="Réinitialiser pour renvoyer"
                            >
                              <RefreshCw size={16} style={{ color: '#FF9800' }} />
                            </button>
                          </>
                        )}
                        {/* Bouton Supprimer */}
                        <button
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                          className="p-2 rounded hover:bg-red-100"
                          title="Supprimer"
                          disabled={campaign.status === 'sending'}
                        >
                          <Trash2 size={16} style={{ color: campaign.status === 'sending' ? '#CCCCCC' : '#DC3545' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {filteredCampaigns.length > pageSize && (
              <div className="flex items-center justify-between px-4 py-3 border-t" style={{ background: '#F9F9F9' }}>
                <div className="text-sm" style={{ color: '#666666' }}>
                  {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredCampaigns.length)} sur {filteredCampaigns.length} campagnes
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-40 hover:bg-gray-100"
                    data-testid="campaigns-prev-page"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: Math.ceil(filteredCampaigns.length / pageSize) }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === Math.ceil(filteredCampaigns.length / pageSize) || Math.abs(p - currentPage) <= 1)
                    .map((p, idx, arr) => (
                      <span key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && <span className="px-1 text-gray-400">...</span>}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`px-3 py-1 text-sm rounded border ${p === currentPage ? 'text-white font-medium' : 'hover:bg-gray-100'}`}
                          style={p === currentPage ? { backgroundColor: '#335C2E', borderColor: '#335C2E' } : {}}
                        >
                          {p}
                        </button>
                      </span>
                    ))
                  }
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredCampaigns.length / pageSize), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredCampaigns.length / pageSize)}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-40 hover:bg-gray-100"
                    data-testid="campaigns-next-page"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}

            {filteredCampaigns.length === 0 && (
              <div className="text-center py-12" style={{ color: '#999999' }}>
                Aucune campagne pour le moment
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Création/Modification Campagne */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>
                {editingCampaign ? 'Modifier la campagne' : 'Nouvelle campagne d\'emailing'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Informations de base */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3" style={{ color: '#333333' }}>Informations de la campagne</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom de la campagne *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full p-2 border rounded"
                      placeholder="Ex: Newsletter Janvier 2024"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full p-2 border rounded"
                    >
                      {appSettings.campaign_types && appSettings.campaign_types.length > 0 ? (
                        appSettings.campaign_types.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))
                      ) : (
                        <>
                          <option value="newsletter">Newsletter</option>
                          <option value="personnalisé">Personnalisé</option>
                          <option value="automatisé">Automatisé</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Objet de l'email *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full p-2 border rounded"
                    placeholder="Ex: Nouvelles de Refacing VR"
                  />
                </div>
              </div>

              {/* Destinataires */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
                  <Users size={18} />
                  Destinataires {recipientCount > 0 ? (
                    <span className="px-2 py-1 rounded text-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                      {recipientCount} contact{recipientCount > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded text-sm" style={{ background: '#FFEBEE', color: '#C62828' }}>
                      Aucun contact
                    </span>
                  )}
                </h3>
                
                {/* Note CASL - Exclusion des désinscrits */}
                <div className="mb-4 p-3 rounded" style={{ background: '#E3F2FD', border: '1px solid #64B5F6' }}>
                  <p className="text-sm" style={{ color: '#1565C0' }}>
                    ℹ️ <strong>Conformité CASL :</strong> Les contacts désinscrits et sans consentement marketing sont automatiquement exclus des envois.
                  </p>
                </div>
                
                {recipientCount === 0 && (
                  <div className="mb-4 p-3 rounded" style={{ background: '#FFF3E0', border: '1px solid #FFB74D' }}>
                    <p className="text-sm" style={{ color: '#E65100' }}>
                      ⚠️ <strong>Attention :</strong> Aucun contact ne correspond aux filtres sélectionnés. 
                      La campagne sera créée mais ne pourra pas être envoyée sans destinataires.
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#F57C00' }}>
                      Vérifiez que vos contacts ont les intérêts et degrés d'intérêt correspondants dans le CRM.
                    </p>
                  </div>
                )}
                
                {!settingsLoaded && (
                  <p className="text-sm text-gray-500 mb-4">Chargement des filtres...</p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Statut</label>
                    <select
                      value={filterStatut}
                      onChange={(e) => setFilterStatut(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Tous</option>
                      {(appSettings.contact_statuts && appSettings.contact_statuts.length > 0 
                        ? appSettings.contact_statuts 
                        : ['prospect', 'lead', 'client', 'inactif']
                      ).map((statut) => (
                        <option key={statut} value={statut}>{statut}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Source</label>
                    <select
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Toutes</option>
                      {(appSettings.contact_sources && appSettings.contact_sources.length > 0 
                        ? appSettings.contact_sources 
                        : ['manuel', 'bd_interne', 'excel_import']
                      ).map((source) => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Tags</label>
                    <input
                      type="text"
                      value={filterTags}
                      onChange={(e) => setFilterTags(e.target.value)}
                      placeholder="Séparés par virgule"
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
                
                {/* Nouveaux filtres: Intérêts et Degré d'intérêt */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Intérêts</label>
                    <div className="border rounded p-2 bg-white max-h-32 overflow-y-auto">
                      {(appSettings.contact_interets && appSettings.contact_interets.length > 0 
                        ? appSettings.contact_interets 
                        : ['Maison de parc', 'UHA']
                      ).map((interet) => (
                        <label key={interet} className="flex items-center gap-2 py-1 hover:bg-gray-50 rounded px-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterInterets.includes(interet)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilterInterets([...filterInterets, interet]);
                              } else {
                                setFilterInterets(filterInterets.filter(i => i !== interet));
                              }
                            }}
                            className="rounded"
                            style={{ accentColor: '#335C2E' }}
                          />
                          <span className="text-sm">{interet}</span>
                        </label>
                      ))}
                    </div>
                    {filterInterets.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {filterInterets.map((interet) => (
                          <span 
                            key={interet}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                            style={{ background: '#E8F5E9', color: '#2E7D32' }}
                          >
                            {interet}
                            <button
                              type="button"
                              onClick={() => setFilterInterets(filterInterets.filter(i => i !== interet))}
                              className="hover:text-red-600"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Degré d'intérêt</label>
                    <select
                      value={filterDegreInteret}
                      onChange={(e) => setFilterDegreInteret(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Tous les degrés</option>
                      {(appSettings.contact_degres_interet && appSettings.contact_degres_interet.length > 0 
                        ? appSettings.contact_degres_interet 
                        : ['Chaud', 'Tiède', 'Froid', 'À qualifier']
                      ).map((degre) => (
                        <option key={degre} value={degre}>{degre}</option>
                      ))}
                    </select>
                    <p className="text-xs mt-1" style={{ color: '#999999' }}>
                      Filtrer par niveau d'intérêt du contact
                    </p>
                  </div>
                </div>
                
                {/* Filtre d'exclusion par campagne */}
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <AlertCircle size={16} className="text-yellow-600" />
                    Exclure les contacts ayant déjà reçu une campagne
                  </label>
                  <select
                    value={filterExcludeCampaign}
                    onChange={(e) => setFilterExcludeCampaign(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="">-- Ne pas exclure --</option>
                    {sentCampaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.sent_count || 0} envoyés le {new Date(campaign.sent_at || campaign.created_at).toLocaleDateString('fr-CA')})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs mt-2 text-yellow-700">
                    ⚠️ Les contacts qui ont déjà reçu cette campagne seront exclus des destinataires
                  </p>
                </div>
                
                <p className="text-xs mt-3" style={{ color: '#666666' }}>
                  💡 Laissez les filtres vides pour envoyer à tous les contacts
                </p>
              </div>

              {/* Contenu */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold" style={{ color: '#333333' }}>Contenu de l'email</h3>
                  <button
                    type="button"
                    onClick={handlePreview}
                    className="text-sm px-3 py-1 rounded"
                    style={{ background: '#2196F3', color: '#FFFFFF' }}
                  >
                    <Eye size={14} className="inline mr-1" />
                    Prévisualiser
                  </button>
                </div>
                
                {/* Sélection de template */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium mb-3" style={{ color: '#333333' }}>
                    📧 Choisir un template
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(emailTemplates).map(([key, template]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setSelectedTemplate(key);
                          setFormData({
                            ...formData,
                            body_html: template.html,
                            body_text: generateDefaultText()
                          });
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedTemplate === key 
                            ? 'border-green-600 bg-green-50' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <p className="font-medium text-sm" style={{ color: selectedTemplate === key ? '#335C2E' : '#333333' }}>
                          {template.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {template.description}
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs mt-3" style={{ color: '#666666' }}>
                    💡 Sélectionnez un template puis personnalisez le contenu HTML ci-dessous
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Contenu HTML *</label>
                  <textarea
                    value={formData.body_html}
                    onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                    required
                    rows="12"
                    className="w-full p-2 border rounded font-mono text-sm"
                    placeholder="Code HTML de l'email..."
                  />
                  <p className="text-xs mt-1" style={{ color: '#666666' }}>
                    Variables disponibles : {'{{'}prenom{'}}'}, {'{{'}nom{'}}'}, {'{{'}email{'}}'}
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#C7A027' }}>
                    ⚠️ Pour ajouter une image de bannière, remplacez l'URL "https://via.placeholder.com/..." par l'URL de votre image
                  </p>
                  
                  {/* Image Uploader */}
                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium mb-2">Ajouter une image</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const uploadFormData = new FormData();
                        uploadFormData.append('file', file);
                        
                        try {
                          toast.info('Upload en cours...', { duration: 2000 });
                          const response = await axios.post(
                            `${API}/upload/image`,
                            uploadFormData,
                            {
                              headers: {
                                Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            }
                          );
                          
                          if (response.data.success) {
                            // Utiliser l'URL publique complète pour les emails
                            const imageUrl = response.data.public_url || `${BACKEND_URL}${response.data.url}`;
                            const imgTag = `<img src="${imageUrl}" alt="Image campagne" style="max-width: 100%; height: auto; display: block; margin: 10px auto;" />`;
                            setFormData(prevData => ({
                              ...prevData,
                              body_html: prevData.body_html + '\n' + imgTag
                            }));
                            toast.success('Image ajoutée au template');
                          }
                        } catch (error) {
                          console.error('Erreur upload:', error);
                          toast.error('Erreur lors de l\'upload: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">L'image sera insérée dans le code HTML</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Contenu texte brut *</label>
                  <textarea
                    value={formData.body_text}
                    onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                    required
                    rows="6"
                    className="w-full p-2 border rounded"
                    placeholder="Version texte brut de l'email..."
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded border"
                  style={{ color: '#666666', borderColor: '#E5E5E5' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                  style={{ background: '#335C2E', color: '#FFFFFF' }}
                >
                  <Save size={18} />
                  {editingCampaign ? 'Enregistrer les modifications' : 'Créer la campagne'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Prévisualisation */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>
                Prévisualisation
              </h2>
              <button onClick={closePreview} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <div className="text-sm font-medium mb-1">Objet :</div>
                <div style={{ color: '#333333' }}>{formData.subject || '(Aucun objet)'}</div>
              </div>
              
              <div className="border rounded p-4" style={{ minHeight: '400px' }}>
                <div dangerouslySetInnerHTML={{ 
                  __html: formData.body_html.replace(/\{\{prenom\}\}/g, 'Jean')
                                              .replace(/\{\{nom\}\}/g, 'Tremblay')
                                              .replace(/\{\{email\}\}/g, 'jean.tremblay@example.com')
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suivi d'envoi en temps réel */}
      {showTrackingModal && trackingData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: '#335C2E' }}>
                <Mail size={24} />
                Suivi de l'envoi
              </h2>
              <button 
                onClick={() => {
                  setShowTrackingModal(false);
                  setTrackingCampaignId(null);
                  fetchCampaigns();
                }} 
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <h3 className="font-semibold mb-4">{trackingData.name}</h3>
              
              {/* Barre de progression */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Progression</span>
                  <span className="font-bold">{trackingData.progress}%</span>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-500 rounded-full"
                    style={{ 
                      width: `${trackingData.progress}%`,
                      background: trackingData.status === 'sending' 
                        ? 'linear-gradient(90deg, #335C2E 0%, #4a7a3f 100%)' 
                        : trackingData.status === 'sent' ? '#4CAF50'
                        : trackingData.status === 'paused' ? '#FFA726'
                        : '#EF5350'
                    }}
                  />
                </div>
              </div>

              {/* Statistiques */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{trackingData.sent}</div>
                  <div className="text-xs text-green-800">Envoyés</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{trackingData.pending}</div>
                  <div className="text-xs text-yellow-800">En attente</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{trackingData.failed}</div>
                  <div className="text-xs text-red-800">Échecs</div>
                </div>
              </div>

              {/* Statut et infos */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Statut</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    trackingData.status === 'sending' ? 'bg-blue-100 text-blue-800' :
                    trackingData.status === 'sent' ? 'bg-green-100 text-green-800' :
                    trackingData.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                    trackingData.status === 'partial' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {trackingData.status === 'sending' && <><RefreshCw size={14} className="inline mr-1 animate-spin" /> En cours</>}
                    {trackingData.status === 'sent' && <><CheckCircle size={14} className="inline mr-1" /> Terminée</>}
                    {trackingData.status === 'paused' && <><Pause size={14} className="inline mr-1" /> En pause</>}
                    {trackingData.status === 'partial' && <><AlertCircle size={14} className="inline mr-1" /> Partielle</>}
                    {trackingData.status === 'failed' && <><AlertCircle size={14} className="inline mr-1" /> Échouée</>}
                    {trackingData.status === 'draft' && 'Brouillon'}
                  </span>
                </div>

                {trackingData.estimated_remaining && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm flex items-center gap-2">
                      <Clock size={16} />
                      Temps restant estimé
                    </span>
                    <span className="font-medium">{trackingData.estimated_remaining}</span>
                  </div>
                )}

                {trackingData.sending_info?.emails_per_hour && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Débit</span>
                    <span className="font-medium">{trackingData.sending_info.emails_per_hour} emails/heure</span>
                  </div>
                )}
              </div>
              
              {/* Liste des destinataires (affiché uniquement pour les campagnes envoyées/terminées) */}
              {trackingData.recipients && trackingData.recipients.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
                    <Users size={16} />
                    Détail des destinataires ({trackingData.recipients.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Contact</th>
                          <th className="text-left p-2 font-medium">Email</th>
                          <th className="text-center p-2 font-medium">Statut</th>
                          <th className="text-left p-2 font-medium">Envoyé à</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackingData.recipients.map((recipient, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2">{recipient.prenom} {recipient.nom}</td>
                            <td className="p-2 text-gray-600">{recipient.email}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                recipient.status === 'sent' ? 'bg-green-100 text-green-800' :
                                recipient.status === 'failed' ? 'bg-red-100 text-red-800' :
                                recipient.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {recipient.status === 'sent' ? '✓ Envoyé' :
                                 recipient.status === 'failed' ? '✗ Échec' :
                                 recipient.status === 'pending' ? '⏳ En attente' :
                                 recipient.status}
                              </span>
                              {recipient.error_message && (
                                <span className="block text-xs text-red-600 mt-1" title={recipient.error_message}>
                                  {recipient.error_message.substring(0, 30)}...
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-gray-500 text-xs">
                              {recipient.sent_at ? new Date(recipient.sent_at).toLocaleString('fr-CA') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-4">
                {trackingData.status === 'sending' && (
                  <button
                    onClick={() => handlePause(trackingCampaignId)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                  >
                    <Pause size={18} />
                    Mettre en pause
                  </button>
                )}
                
                {trackingData.status === 'paused' && (
                  <button
                    onClick={() => handleResume(trackingCampaignId)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90"
                    style={{ background: '#335C2E' }}
                  >
                    <Play size={18} />
                    Reprendre
                  </button>
                )}

                {['sent', 'partial', 'failed'].includes(trackingData.status) && (
                  <button
                    onClick={() => {
                      setShowTrackingModal(false);
                      fetchCampaigns();
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white"
                    style={{ background: '#335C2E' }}
                  >
                    <CheckCircle size={18} />
                    Fermer
                  </button>
                )}
              </div>

              {/* Message informatif */}
              {trackingData.status === 'sending' && (
                <p className="mt-4 text-xs text-center text-gray-500">
                  L'envoi continue en arrière-plan. Vous pouvez fermer cette fenêtre.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Voir le contenu d'une campagne */}
      {viewingCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>
                  Contenu de la campagne
                </h2>
                <p className="text-sm text-gray-500">{viewingCampaign.name}</p>
              </div>
              <button onClick={() => setViewingCampaign(null)} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-grow">
              {/* Informations de la campagne */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Statut</div>
                  <div className="font-semibold capitalize">
                    {viewingCampaign.status === 'draft' ? 'Brouillon' :
                     viewingCampaign.status === 'sending' ? 'En cours' :
                     viewingCampaign.status === 'sent' ? 'Envoyée' : 
                     viewingCampaign.status === 'partial' ? 'Partielle' :
                     viewingCampaign.status === 'failed' ? 'Échec' : viewingCampaign.status}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Destinataires</div>
                  <div className="font-semibold">{viewingCampaign.recipients_count || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Envoyés</div>
                  <div className="font-semibold text-green-600">{viewingCampaign.sent_count || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Date création</div>
                  <div className="font-semibold text-sm">
                    {viewingCampaign.created_at ? new Date(viewingCampaign.created_at).toLocaleDateString('fr-CA') : '-'}
                  </div>
                </div>
              </div>

              {/* Objet de l'email */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-800 mb-1">📧 Objet de l'email :</div>
                <div className="text-blue-900 font-semibold">{viewingCampaign.subject || '(Aucun objet)'}</div>
              </div>

              {/* Contenu HTML */}
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Aperçu du contenu HTML :</div>
                <div 
                  className="border rounded-lg p-4 bg-white overflow-auto"
                  style={{ maxHeight: '500px' }}
                >
                  <div dangerouslySetInnerHTML={{ 
                    __html: (viewingCampaign.body_html || '<p style="color: #999;">Aucun contenu HTML</p>')
                      .replace(/\{\{prenom\}\}/g, '<span style="background:#fef3c7;padding:2px 4px;border-radius:3px;">Jean</span>')
                      .replace(/\{\{nom\}\}/g, '<span style="background:#fef3c7;padding:2px 4px;border-radius:3px;">Tremblay</span>')
                      .replace(/\{\{email\}\}/g, '<span style="background:#fef3c7;padding:2px 4px;border-radius:3px;">jean@example.com</span>')
                  }} />
                </div>
              </div>

              {/* Contenu texte (si disponible) */}
              {viewingCampaign.body_text && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Version texte brut :</div>
                  <pre className="border rounded-lg p-4 bg-gray-50 text-sm whitespace-pre-wrap font-sans overflow-auto" style={{ maxHeight: '200px' }}>
                    {viewingCampaign.body_text
                      .replace(/\{\{prenom\}\}/g, 'Jean')
                      .replace(/\{\{nom\}\}/g, 'Tremblay')
                      .replace(/\{\{email\}\}/g, 'jean@example.com')}
                  </pre>
                </div>
              )}
            </div>

            <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => {
                  openEditModal(viewingCampaign);
                  setViewingCampaign(null);
                }}
                className="px-4 py-2 rounded-lg border hover:bg-gray-100 flex items-center gap-2"
                disabled={viewingCampaign.status !== 'draft'}
              >
                <Edit size={16} />
                Modifier
              </button>
              <button
                onClick={() => setViewingCampaign(null)}
                className="px-4 py-2 rounded-lg text-white"
                style={{ background: '#335C2E' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Journal des échecs */}
      {showFailuresModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: '#DC2626' }}>
                  <AlertTriangle size={24} />
                  Journal des échecs d'envoi
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedFailureCampaign 
                    ? `Campagne: ${failuresData?.campaign_name || selectedFailureCampaign}` 
                    : 'Toutes les campagnes'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedFailureCampaign && (
                  <button 
                    onClick={() => fetchAllFailures()}
                    className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50"
                  >
                    Voir tous les échecs
                  </button>
                )}
                <button onClick={() => setShowFailuresModal(false)} className="p-2 hover:bg-gray-100 rounded">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-grow">
              {failuresLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw size={24} className="animate-spin text-gray-400" />
                </div>
              ) : !failuresData || (failuresData.failures?.length === 0 && failuresData.total_failures === 0) ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-medium text-green-700">Aucun échec d'envoi !</p>
                  <p className="text-sm">Tous les emails ont été envoyés avec succès.</p>
                </div>
              ) : (
                <>
                  {/* Statistiques */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="text-2xl font-bold text-red-600">
                        {failuresData.total_failures || failuresData.failures?.length || 0}
                      </div>
                      <div className="text-xs text-red-500">Échecs totaux</div>
                    </div>
                    {failuresData.total_recipients && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">
                          {failuresData.total_recipients}
                        </div>
                        <div className="text-xs text-blue-500">Destinataires totaux</div>
                      </div>
                    )}
                    {failuresData.success_rate !== undefined && (
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="text-2xl font-bold text-green-600">
                          {failuresData.success_rate}%
                        </div>
                        <div className="text-xs text-green-500">Taux de succès</div>
                      </div>
                    )}
                  </div>

                  {/* Statistiques par type d'erreur */}
                  {failuresData.error_statistics && Object.keys(failuresData.error_statistics).length > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <FileText size={16} />
                        Répartition par type d'erreur
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(failuresData.error_statistics).map(([type, count]) => (
                          <span 
                            key={type} 
                            className="px-3 py-1 rounded-full text-sm font-medium"
                            style={{ 
                              backgroundColor: type.includes('SMTP') ? '#FEE2E2' : 
                                               type.includes('Connexion') ? '#FEF3C7' :
                                               type.includes('Destinataire') ? '#DBEAFE' :
                                               type.includes('Spam') ? '#FCE7F3' :
                                               type.includes('Limite') ? '#F3E8FF' : '#F3F4F6',
                              color: type.includes('SMTP') ? '#DC2626' : 
                                     type.includes('Connexion') ? '#D97706' :
                                     type.includes('Destinataire') ? '#2563EB' :
                                     type.includes('Spam') ? '#DB2777' :
                                     type.includes('Limite') ? '#7C3AED' : '#6B7280'
                            }}
                          >
                            {type}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Liste des échecs */}
                  <div className="space-y-3">
                    {(failuresData.failures || []).map((failure, index) => (
                      <div 
                        key={index} 
                        className="border rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                      >
                        <div 
                          className="p-4 cursor-pointer flex items-start justify-between"
                          onClick={() => toggleFailureExpand(index)}
                          style={{ backgroundColor: expandedFailures[index] ? '#FEF2F2' : '#FFFFFF' }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                              <XCircle size={16} className="text-red-500" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {failure.name || failure.email}
                              </div>
                              <div className="text-sm text-gray-500">{failure.email}</div>
                              {failure.campaign_name && !selectedFailureCampaign && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Campagne: {failure.campaign_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                              {failure.error_category || failure.probable_cause || 'Erreur'}
                            </span>
                            <ChevronRight 
                              size={16} 
                              className={`text-gray-400 transition-transform ${expandedFailures[index] ? 'rotate-90' : ''}`}
                            />
                          </div>
                        </div>
                        
                        {expandedFailures[index] && (
                          <div className="px-4 pb-4 pt-2 border-t bg-red-50">
                            <div className="space-y-3">
                              {/* Message d'erreur */}
                              <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Message d'erreur:</div>
                                <div className="text-sm text-red-700 bg-white p-2 rounded border border-red-200 font-mono">
                                  {failure.error_message}
                                </div>
                              </div>
                              
                              {/* Cause probable */}
                              {failure.probable_cause && (
                                <div>
                                  <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                    <HelpCircle size={12} />
                                    Cause probable:
                                  </div>
                                  <div className="text-sm text-orange-700 font-medium">
                                    {failure.probable_cause}
                                  </div>
                                </div>
                              )}
                              
                              {/* Suggestions */}
                              {failure.suggestions && failure.suggestions.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-gray-500 mb-1">Solutions suggérées:</div>
                                  <ul className="text-sm text-gray-600 space-y-1">
                                    {failure.suggestions.map((suggestion, i) => (
                                      <li key={i} className="flex items-start gap-2">
                                        <span className="text-green-500 mt-0.5">•</span>
                                        {suggestion}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Lien vers le contact */}
                              {failure.contact_id && (
                                <div className="pt-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`/admin/crm?contact=${failure.contact_id}`, '_blank');
                                    }}
                                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    <Users size={14} />
                                    Voir le contact dans le CRM
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="border-t px-6 py-4 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {failuresData?.failures?.length || 0} échec(s) affiché(s)
              </div>
              <button
                onClick={() => setShowFailuresModal(false)}
                className="px-4 py-2 rounded-lg text-white"
                style={{ background: '#335C2E' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
